/**
 * Shared helpers for the "Document Signature & Seal" settings
 * (General Settings → Documents tab).
 *
 * The signature/seal images themselves are shared with invoices and configured
 * on the Invoice tab. This module only resolves *whether* they should be added
 * to a specific document, so each document type can be allowed individually.
 */

/** Global default; used for any document type without its own per-type key. */
export const DOCUMENTS_SIGNATURE_ENABLED_KEY = 'documents_signature_enabled';

/** Every non-invoice document that can carry the shared signature & seal. */
export const DOCUMENT_SIGNATURE_TARGETS = [
  { key: 'admission', label: 'Admission & discharge papers' },
  { key: 'salary_sheet', label: 'Bank salary sheet' },
  { key: 'expense_voucher', label: 'Expense payment voucher' },
  { key: 'doctor_voucher', label: 'Doctor payment voucher' },
  { key: 'payslip', label: 'Employee payslip' },
  { key: 'diagnostic_receipt', label: 'Diagnostic receipt' },
] as const;

export type DocumentSignatureTarget = (typeof DOCUMENT_SIGNATURE_TARGETS)[number]['key'];

/** Per-document override key, e.g. `documents_signature_expense_voucher`. */
export function documentSignatureKey(target: DocumentSignatureTarget): string {
  return `documents_signature_${target}`;
}

/**
 * Resolves whether the signature/seal should be added to a specific document.
 * A per-document key wins when set; otherwise the global
 * {@link DOCUMENTS_SIGNATURE_ENABLED_KEY} value is used as the default.
 *
 * `get` is a lookup for a setting value (empty string when unset).
 */
export function isDocumentSignatureEnabled(get: (key: string) => string, target: DocumentSignatureTarget): boolean {
  const specific = get(documentSignatureKey(target));
  if (specific === 'true') return true;
  if (specific === 'false') return false;
  return get(DOCUMENTS_SIGNATURE_ENABLED_KEY) === 'true';
}
